import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Briefcase,
  Building2,
  CalendarClock,
  CalendarX,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  HeartPulse,
  FileText,
  Info,
  Loader2,
  MapPin,
  Pencil,
  RefreshCw,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Archive,
  Upload,
  UserMinus,
  UserPlus,
  Users,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { StatCardsSkeleton, TableSkeleton } from "@/components/PageSkeleton";
import { DatePicker } from "@/components/ui/DatePicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { getMedicalCheckDetailDescriptionKey } from "@/lib/medical-check-display";
import {
  apiAddToMedicalCheckGroup,
  apiArchiveMedicalCheckAssignment,
  apiCancelMedicalCheckAssignment,
  apiCompleteMedicalCheckAssignment,
  apiCreateMedicalCheckAssignments,
  apiDeleteMedicalCheckRule,
  apiGetMedicalChecks,
  apiGetMedicalCheckAssignment,
  apiRemoveFromMedicalCheckGroup,
  apiRecordMedicalCheckGroupAttendance,
  apiRespondToMedicalCheckAssignment,
  apiUpdateMedicalCheckCompanySettings,
  apiUpdateMedicalCheckAssignment,
  apiUpdateMedicalCheckRule,
  apiCreateHRDocument,
  apiUploadHRDocumentFile,
  apiGetPositions,
  apiDownloadHRDocumentFile,
  type MedicalCheckAssignment,
  type MedicalCheckEmployeeRecord,
  type MedicalCheckStatus,
} from "@/lib/api";

type StatusFilter = MedicalCheckStatus | "all";
type MedicalTab = "overview" | "assignments" | "rejected" | "history" | "settings";

const STATUS_STYLES: Record<MedicalCheckStatus, string> = {
  valid: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
  due_soon: "bg-amber-500/10 text-amber-700 border-amber-200",
  expired: "bg-red-500/10 text-red-700 border-red-200",
  failed: "bg-rose-500/10 text-rose-700 border-rose-200",
  no_exam: "bg-slate-500/10 text-slate-700 border-slate-200",
};

function fmtDate(value?: string | null) {
  if (!value) return "-";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : String(value);
}

function fmtDateTime(date?: string | null, time?: string | null) {
  if (!date) return "-";
  const d = fmtDate(date);
  return time ? `${d} · ${String(time).slice(0, 5)}` : d;
}

function StatusBadge({ status }: { status: MedicalCheckStatus }) {
  const { t } = useTranslation();
  return (
    <Badge variant="outline" className={STATUS_STYLES[status]}>
      {t(`medicalCheckups.status.${status}`)}
    </Badge>
  );
}

function SourceBadge({ record }: { record: MedicalCheckEmployeeRecord }) {
  const { t } = useTranslation();
  const label = record.frequencySource === "position"
    ? t("medicalCheckups.source.position", { name: record.positionTitle ?? "—" })
    : record.frequencySource === "team"
    ? t("medicalCheckups.source.team", { name: record.team ?? "—" })
    : record.frequencySource === "department"
      ? t("medicalCheckups.source.department", { name: record.department ?? "—" })
      : t("medicalCheckups.source.company");
  const tone =
    record.frequencySource === "position" ? "border-emerald-200 text-emerald-700 bg-emerald-500/10" :
    record.frequencySource === "team" ? "border-violet-200 text-violet-700 bg-violet-500/10" :
    record.frequencySource === "department" ? "border-blue-200 text-blue-700 bg-blue-500/10" :
    "border-gray-200 text-gray-600 bg-gray-100";
  return <Badge variant="outline" className={`text-[10px] ${tone}`}>{label}</Badge>;
}

function StatSegment({
  icon: Icon, label, value, bg, color, active, onClick,
}: {
  icon: any; label: string; value: number; bg: string; color: string;
  active?: boolean; onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3.5 px-5 py-4 text-left transition-colors w-full ${
        active ? "bg-muted/60" : "hover:bg-muted/40"
      }`}
    >
      <div className={`h-9 w-9 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <p className="text-2xl font-bold tabular-nums text-foreground leading-none">{value}</p>
      </div>
      {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
    </button>
  );
}

// Returns a stable gradient class for an employee name (used by the inline avatar)
const AVATAR_GRADIENTS = [
  "from-blue-400 to-blue-600",
  "from-green-400 to-emerald-600",
  "from-purple-500 to-indigo-500",
  "from-amber-400 to-orange-500",
  "from-red-500 to-rose-500",
  "from-cyan-400 to-teal-500",
];
function avatarColorFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length];
}
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

function InitialsAvatar({ name, size = "sm" }: { name: string; size?: "sm" | "md" }) {
  const sz = size === "md" ? "h-10 w-10 text-sm" : "h-8 w-8 text-xs";
  return (
    <div className={`${sz} rounded-full flex items-center justify-center text-white font-semibold shrink-0 bg-gradient-to-br ${avatarColorFor(name)}`}>
      {initialsOf(name)}
    </div>
  );
}

export default function MedicalCheckups() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isHrAdmin = user?.role?.mainRole === "hr" || user?.role?.mainRole === "admin";

  const [tab, setTab] = useState<MedicalTab>(isHrAdmin ? "overview" : "overview");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Assignment dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignEmployeeIds, setAssignEmployeeIds] = useState<number[]>([]);
  const [assignDate, setAssignDate] = useState("");
  const [assignTime, setAssignTime] = useState("");
  const [assignInstitution, setAssignInstitution] = useState("");
  const [assignNotes, setAssignNotes] = useState("");
  const [assignSearch, setAssignSearch] = useState("");
  const [assignSubmitted, setAssignSubmitted] = useState(false);

  // Edit/reschedule state. Group scope updates only still-active members and
  // leaves completed examination records untouched.
  const [editingAppointment, setEditingAppointment] = useState<{ assignment: MedicalCheckAssignment; scope: "individual" | "group" } | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editInstitution, setEditInstitution] = useState("");
  const [editNotes, setEditNotes] = useState("");

  // Detail dialog state (opens via ?assignment=<id> deep link from a notification)
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailAssignmentId, setDetailAssignmentId] = useState<number | null>(null);

  // Reject dialog state (employee)
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectAssignmentId, setRejectAssignmentId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  // Tracks which rejected assignment ID is being rescheduled (so we can flip its badge instantly on success)
  const [reschedulingFromId, setReschedulingFromId] = useState<number | null>(null);
  // Optimistic set — rejected assignment IDs that were rescheduled in this session (instant badge, no round-trip wait)
  const [locallyRescheduledIds, setLocallyRescheduledIds] = useState<Set<number>>(new Set());

  // HR Result Upload / Completion Dialog State
  const [completingAssignment, setCompletingAssignment] = useState<MedicalCheckAssignment | null>(null);
  const [completeResult, setCompleteResult] = useState<"passed" | "failed">("passed");
  const [completeFile, setCompleteFile] = useState<File | null>(null);
  const [isSubmittingComplete, setIsSubmittingComplete] = useState(false);
  const completeFileInputRef = useRef<HTMLInputElement>(null);

  // Settings drafts
  const [settingsDrafts, setSettingsDrafts] = useState<{ intervalMonths?: string; graceMonths?: string; leadMonths?: string }>({});
  const [deptDrafts, setDeptDrafts] = useState<Record<string, string>>({});
  const [teamDrafts, setTeamDrafts] = useState<Record<string, string>>({});
  const [positionDrafts, setPositionDrafts] = useState<Record<number, string>>({});

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["medical-checks"],
    queryFn: apiGetMedicalChecks,
  });

  // Positions list — used to configure per-position frequency overrides.
  const { data: positions = [] } = useQuery({
    queryKey: ["positions"],
    queryFn: apiGetPositions,
  });

  // Open detail dialog whenever the URL carries ?assignment=<id>
  useEffect(() => {
    const aId = searchParams.get("assignment");
    if (aId) {
      const id = Number(aId);
      if (Number.isInteger(id) && id > 0) {
        setDetailAssignmentId(id);
        setDetailOpen(true);
      }
    }
  }, [searchParams]);

  function closeDetail() {
    setDetailOpen(false);
    setDetailAssignmentId(null);
    if (searchParams.has("assignment")) {
      const next = new URLSearchParams(searchParams);
      next.delete("assignment");
      next.delete("legacyEmployeeId");
      setSearchParams(next, { replace: true });
    }
  }

  const employees = useMemo(() => data?.employees ?? [], [data?.employees]);
  const assignments = useMemo(() => data?.assignments ?? [], [data?.assignments]);
  const rules = useMemo(() => data?.rules ?? [], [data?.rules]);
  const settings = data?.settings;
  const stats = data?.stats ?? { total: 0, valid: 0, due_soon: 0, expired: 0, failed: 0, no_exam: 0 };

  const departments = useMemo(() => {
    const set = new Set<string>();
    data?.departments?.forEach(department => set.add(department.name));
    employees.forEach(e => { if (e.department) set.add(e.department); });
    return Array.from(set).sort();
  }, [data?.departments, employees]);

  const departmentIdByName = useMemo(() => {
    const map = new Map<string, number>();
    data?.departments?.forEach(department => map.set(department.name, department.id));
    employees.forEach(e => { if (e.department && e.departmentId) map.set(e.department, e.departmentId); });
    return map;
  }, [data?.departments, employees]);

  const teams = useMemo(() => {
    const set = new Set<string>();
    data?.teams?.forEach(team => set.add(team));
    employees.forEach(e => { if (e.team) set.add(e.team); });
    return Array.from(set).sort();
  }, [data?.teams, employees]);

  const departmentRules = useMemo(() => rules.filter(r => r.scope === "department"), [rules]);
  const teamRules = useMemo(() => rules.filter(r => r.scope === "team"), [rules]);
  const positionRules = useMemo(() => rules.filter(r => r.scope === "position"), [rules]);

  const filteredEmployees = useMemo(() => {
    return employees.filter(e => {
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (departmentFilter !== "all" && e.department !== departmentFilter) return false;
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const hay = `${e.employeeName} ${e.employeeEmail ?? ""} ${e.department ?? ""} ${e.team ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [employees, statusFilter, departmentFilter, searchTerm]);

  const rejectedAssignments = useMemo(() => assignments.filter(a => a.status === "rejected"), [assignments]);
  const openAssignments = useMemo(
    () => assignments.filter(a => a.status === "pending" || a.status === "accepted" || a.status === "attended"),
    [assignments],
  );
  const historyAssignments = useMemo(
    () => assignments.filter(a => ["completed", "passed", "failed", "absent", "cancelled"].includes(a.status)),
    [assignments],
  );

  function localizedMedicalError(error: any, fallbackKey: string): string {
    const byCode: Record<string, string> = {
      MEDICAL_CHECK_ACTIVE_ASSIGNMENT: "medicalCheckups.messages.activeAssignmentExists",
      MEDICAL_CHECK_GROUP_ALREADY_COMPLETED: "medicalCheckups.messages.alreadyCompletedGroup",
      MEDICAL_CHECK_GROUP_DUPLICATE: "medicalCheckups.messages.alreadyInGroup",
      MEDICAL_CHECK_TOO_EARLY: "medicalCheckups.messages.tooEarly",
      MEDICAL_CHECK_REQUIRED_FIELDS: "medicalCheckups.validation.completeRequired",
      MEDICAL_CHECK_TIME_REQUIRED: "medicalCheckups.validation.timeRequired",
      MEDICAL_CHECK_NOT_EDITABLE: "medicalCheckups.messages.notEditable",
      MEDICAL_CHECK_NOT_ARCHIVABLE: "medicalCheckups.messages.archiveFailed",
      MEDICAL_CHECK_DATE_INVALID: "medicalCheckups.validation.dateInvalid",
      MEDICAL_CHECK_INSTITUTION_REQUIRED: "medicalCheckups.validation.institutionRequired",
      MEDICAL_CHECK_INVALID_ATTENDEE: "medicalCheckups.messages.invalidAttendee",
      MEDICAL_CHECK_GROUP_NOT_ACTIVE: "medicalCheckups.messages.groupNotActive",
    };
    return t(byCode[error?.code] ?? fallbackKey);
  }

  // ── Mutations ───────────────────────────────────────────────────────────────
  const assignMutation = useMutation({
    mutationFn: apiCreateMedicalCheckAssignments,
    onSuccess: (res) => {
      toast.success(t("medicalCheckups.messages.assignmentsCreated", { count: res.created }));
      setAssignOpen(false);
      setAssignEmployeeIds([]);
      setAssignDate("");
      setAssignTime("");
      setAssignInstitution("");
      setAssignNotes("");
      setAssignSubmitted(false);
      // If this mutation came from a reschedule action, immediately flip the badge
      // without waiting for the query refetch (optimistic update).
      if (reschedulingFromId !== null) {
        setLocallyRescheduledIds(prev => new Set([...prev, reschedulingFromId]));
        setReschedulingFromId(null);
      }
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.assignmentsFailed")),
  });

  const respondMutation = useMutation({
    mutationFn: ({ id, action, rejectionReason }: { id: number; action: "accept" | "reject"; rejectionReason?: string }) =>
      apiRespondToMedicalCheckAssignment(id, { action, rejectionReason }),
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.responseRecorded"));
      setRejectOpen(false);
      setRejectAssignmentId(null);
      setRejectReason("");
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.responseFailed")),
  });

  const completeMutation = useMutation({
    mutationFn: ({ id, result, supportingDocumentId }: { id: number; result: "passed" | "failed"; supportingDocumentId?: number | null }) =>
      apiCompleteMedicalCheckAssignment(id, { result, supportingDocumentId }),
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.completed"));
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
      setCompletingAssignment(null);
      setCompleteFile(null);
      setCompleteResult("passed");
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.completeFailed")),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, scope }: { id: number; scope: "individual" | "group" }) => apiCancelMedicalCheckAssignment(id, scope),
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.cancelled"));
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.cancelFailed")),
  });

  const addToGroupMutation = useMutation({
    mutationFn: ({ groupId, employeeIds }: { groupId: number; employeeIds: number[] }) =>
      apiAddToMedicalCheckGroup(groupId, employeeIds),
    onSuccess: (result) => {
      toast.success(t("medicalCheckups.messages.membersAdded", { count: result.added }));
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => {
      if (err?.code === "MEDICAL_CHECK_GROUP_ALREADY_COMPLETED") {
        toast.error(t("medicalCheckups.messages.alreadyCompletedGroup"));
      } else if (err?.code === "MEDICAL_CHECK_GROUP_DUPLICATE") {
        toast.error(t("medicalCheckups.messages.alreadyInGroup"));
      } else {
        toast.error(localizedMedicalError(err, "medicalCheckups.messages.memberAddFailed"));
      }
    },
  });

  const recordGroupAttendanceMutation = useMutation({
    mutationFn: ({ groupId, attendeeAssignmentIds }: { groupId: number; attendeeAssignmentIds: number[] }) =>
      apiRecordMedicalCheckGroupAttendance(groupId, attendeeAssignmentIds),
    onSuccess: (result) => {
      toast.success(t("medicalCheckups.messages.attendanceRecorded", { attended: result.attended, absent: result.absent }));
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.attendanceFailed")),
  });

  const updateAppointmentMutation = useMutation({
    mutationFn: ({ id, scope, scheduledDate, scheduledTime, institution, notes }: {
      id: number; scope: "individual" | "group"; scheduledDate: string; scheduledTime: string; institution: string; notes?: string | null;
    }) => apiUpdateMedicalCheckAssignment(id, { scope, scheduledDate, scheduledTime, institution, notes }),
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.appointmentUpdated"));
      setEditingAppointment(null);
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.appointmentUpdateFailed")),
  });

  const archiveMutation = useMutation({
    mutationFn: apiArchiveMedicalCheckAssignment,
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.archived"));
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.archiveFailed")),
  });

  const removeFromGroupMutation = useMutation({
    mutationFn: ({ groupId, assignmentId }: { groupId: number; assignmentId: number }) =>
      apiRemoveFromMedicalCheckGroup(groupId, assignmentId),
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.memberRemoved"));
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.memberRemoveFailed")),
  });

  const companySettingsMutation = useMutation({
    mutationFn: apiUpdateMedicalCheckCompanySettings,
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.settingsSaved"));
      setSettingsDrafts({});
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.settingsFailed")),
  });

  const deptRuleMutation = useMutation({
    mutationFn: (p: { departmentId: number; frequencyMonths: number }) =>
      apiUpdateMedicalCheckRule({ departmentId: p.departmentId }, p.frequencyMonths),
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.ruleSaved"));
      setDeptDrafts({});
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.ruleFailed")),
  });

  const teamRuleMutation = useMutation({
    mutationFn: (p: { teamName: string; frequencyMonths: number }) =>
      apiUpdateMedicalCheckRule({ teamName: p.teamName }, p.frequencyMonths),
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.ruleSaved"));
      setTeamDrafts({});
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.ruleFailed")),
  });

  const positionRuleMutation = useMutation({
    mutationFn: (p: { positionId: number; frequencyMonths: number }) =>
      apiUpdateMedicalCheckRule({ positionId: p.positionId }, p.frequencyMonths),
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.ruleSaved"));
      setPositionDrafts({});
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.ruleFailed")),
  });

  const deleteRuleMutation = useMutation({
    mutationFn: (scope: { departmentId: number } | { teamName: string } | { positionId: number }) => apiDeleteMedicalCheckRule(scope),
    onSuccess: () => {
      toast.success(t("medicalCheckups.messages.ruleReset"));
      setDeptDrafts({});
      setTeamDrafts({});
      setPositionDrafts({});
      qc.invalidateQueries({ queryKey: ["medical-checks"] });
    },
    onError: (err: any) => toast.error(localizedMedicalError(err, "medicalCheckups.messages.ruleFailed")),
  });

  // ── Assign dialog: candidate employees ──────────────────────────────────────
  const assignCandidates = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    const activeEmployeeIds = new Set(openAssignments.map(a => a.employeeId));
    return employees
      .filter(e => {
        if (activeEmployeeIds.has(e.employeeId) && !assignEmployeeIds.includes(e.employeeId)) return false;
        if (!q) return true;
        const hay = `${e.employeeName} ${e.employeeEmail ?? ""} ${e.department ?? ""} ${e.team ?? ""}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }, [employees, assignSearch, openAssignments, assignEmployeeIds]);

  const assignmentErrors = {
    employees: assignEmployeeIds.length === 0,
    date: !assignDate,
    time: !assignTime,
    institution: !assignInstitution.trim(),
  };

  function openAppointmentEditor(assignment: MedicalCheckAssignment, scope: "individual" | "group") {
    setEditingAppointment({ assignment, scope });
    setEditDate(assignment.scheduledDate.slice(0, 10));
    setEditTime(assignment.scheduledTime?.slice(0, 5) ?? "");
    setEditInstitution(assignment.institution);
    setEditNotes(assignment.notes ?? "");
  }

  // The current user's own employee_id (resolved by email-match against the
  // dashboard payload). HR managers are also employees, so they may be the
  // assignee of a medical-check assignment and must be able to Accept/Reject
  // their own — independent of their HR role.
  const currentUserEmployeeId = useMemo(() => {
    if (!user?.email) return null;
    return employees.find(e => e.employeeEmail === user.email)?.employeeId ?? null;
  }, [employees, user?.email]);

  const companySettingsHaveChanges = Object.entries(settingsDrafts).some(([key, value]) => {
    const currentByKey: Record<string, number | undefined> = {
      intervalMonths: settings?.intervalMonths,
      graceMonths: settings?.graceMonths,
      leadMonths: settings?.leadMonths,
    };
    const current = currentByKey[key];
    return value !== undefined && Number(value) !== Number(current);
  });

  // ── Employee personal view (non-HR) ─────────────────────────────────────────
  const myRecord = useMemo(() => {
    if (isHrAdmin) return null;
    return employees[0] ?? null;
  }, [employees, isHrAdmin]);

  const myPendingAssignments = useMemo(() => {
    if (isHrAdmin) return [];
    return assignments.filter(a => a.status === "pending" || a.status === "accepted");
  }, [assignments, isHrAdmin]);

  // ── HR Overview ─────────────────────────────────────────────────────────────
  const hrView = (
    <>
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 divide-x divide-y divide-border/60">
          <StatSegment
            icon={ShieldCheck}
            label={t("medicalCheckups.stats.valid")}
            value={stats.valid}
            bg="bg-emerald-500/10"
            color="text-emerald-600"
            active={statusFilter === "valid"}
            onClick={() => { setStatusFilter(statusFilter === "valid" ? "all" : "valid"); setTab("overview"); }}
          />
          <StatSegment
            icon={CalendarClock}
            label={t("medicalCheckups.stats.dueSoon")}
            value={stats.due_soon}
            bg="bg-amber-500/10"
            color="text-amber-600"
            active={statusFilter === "due_soon"}
            onClick={() => { setStatusFilter(statusFilter === "due_soon" ? "all" : "due_soon"); setTab("overview"); }}
          />
          <StatSegment
            icon={AlertTriangle}
            label={t("medicalCheckups.stats.expired")}
            value={stats.expired}
            bg="bg-red-500/10"
            color="text-red-600"
            active={statusFilter === "expired"}
            onClick={() => { setStatusFilter(statusFilter === "expired" ? "all" : "expired"); setTab("overview"); }}
          />
          <StatSegment
            icon={Users}
            label={t("medicalCheckups.stats.failed")}
            value={stats.failed}
            bg="bg-rose-500/10"
            color="text-rose-600"
            active={statusFilter === "failed"}
            onClick={() => { setStatusFilter(statusFilter === "failed" ? "all" : "failed"); setTab("overview"); }}
          />
          <StatSegment
            icon={FileText}
            label={t("medicalCheckups.stats.noExam")}
            value={stats.no_exam}
            bg="bg-slate-500/10"
            color="text-slate-600"
            active={statusFilter === "no_exam"}
            onClick={() => { setStatusFilter(statusFilter === "no_exam" ? "all" : "no_exam"); setTab("overview"); }}
          />
          <StatSegment
            icon={Users}
            label={t("medicalCheckups.stats.totalTracked")}
            value={stats.total}
            bg="bg-sky-500/10"
            color="text-sky-600"
            active={statusFilter === "all"}
            onClick={() => { setStatusFilter("all"); setTab("overview"); }}
          />
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as MedicalTab)} className="space-y-4">
        <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
          <TabsTrigger value="overview" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
            {t("medicalCheckups.tabs.overview")}
          </TabsTrigger>
          <TabsTrigger value="assignments" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
            {t("medicalCheckups.tabs.assignments")}
            {openAssignments.length > 0 && <Badge variant="secondary" className="text-[10px] ml-1">{openAssignments.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="rejected" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
            {t("medicalCheckups.tabs.rejected")}
            {rejectedAssignments.length > 0 && <Badge variant="secondary" className="text-[10px] ml-1">{rejectedAssignments.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
            {t("medicalCheckups.tabs.history")}
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
            {t("medicalCheckups.tabs.settings")}
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ───────────────────────────────────────────────────── */}
        <TabsContent value="overview" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("medicalCheckups.table.searchPlaceholder") as string}
                className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm"
              />
            </div>
            <select
              className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <option value="all">{t("medicalCheckups.filters.allStatuses")}</option>
              <option value="valid">{t("medicalCheckups.status.valid")}</option>
              <option value="due_soon">{t("medicalCheckups.status.due_soon")}</option>
              <option value="expired">{t("medicalCheckups.status.expired")}</option>
              <option value="failed">{t("medicalCheckups.status.failed")}</option>
              <option value="no_exam">{t("medicalCheckups.status.no_exam")}</option>
            </select>
            <select
              className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="all">{t("medicalCheckups.filters.allDepartments")}</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <div className="ml-auto flex items-center gap-2">
              <Button onClick={() => { setReschedulingFromId(null); setAssignEmployeeIds([]); setAssignDate(""); setAssignTime(""); setAssignInstitution(""); setAssignNotes(""); setAssignSearch(""); setAssignSubmitted(false); setAssignOpen(true); }} size="sm" className="gap-1.5">
                <Send className="h-3.5 w-3.5" />{t("medicalCheckups.actions.assignNew")}
              </Button>
              <Button onClick={() => refetch()} size="sm" variant="outline" disabled={isFetching}>
                <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
          <RecordsTable records={filteredEmployees} />
        </TabsContent>

        {/* ── Assignments (open: pending/accepted) ───────────────────────── */}
        <TabsContent value="assignments" className="space-y-4">
          <AssignmentsTable
            assignments={openAssignments}
            allAssignments={assignments}
            employees={employees}
            onOpen={(id) => { setDetailAssignmentId(id); setDetailOpen(true); }}
            onCancel={(id) => {
              if (window.confirm(t("medicalCheckups.confirm.cancelIndividual"))) cancelMutation.mutate({ id, scope: "individual" });
            }}
            onCancelGroup={(id) => {
              if (window.confirm(t("medicalCheckups.confirm.cancelGroup"))) cancelMutation.mutate({ id, scope: "group" });
            }}
            onEdit={(assignment, scope) => openAppointmentEditor(assignment, scope)}
            onRecordAttendance={(groupId, attendeeAssignmentIds) =>
              recordGroupAttendanceMutation.mutateAsync({ groupId, attendeeAssignmentIds })}
            onAddToGroup={(groupId, employeeIds) => addToGroupMutation.mutate({ groupId, employeeIds })}
            onAddNewToSlot={(slot) => assignMutation.mutate(slot)}
            onRemoveFromGroup={(groupId, assignmentId) => {
              if (window.confirm(t("medicalCheckups.confirm.removeIndividual"))) removeFromGroupMutation.mutate({ groupId, assignmentId });
            }}
          />
        </TabsContent>

        {/* ── Rejected ───────────────────────────────────────────────────── */}
        <TabsContent value="rejected" className="space-y-4">
          <RejectedTable
            assignments={rejectedAssignments}
            openAssignments={openAssignments}
            locallyRescheduledIds={locallyRescheduledIds}
            onOpen={(id) => { setDetailAssignmentId(id); setDetailOpen(true); }}
            onArchive={(id) => {
              if (window.confirm(t("medicalCheckups.confirm.archiveDeclined"))) archiveMutation.mutate(id);
            }}
            onReschedule={(a) => {
              setReschedulingFromId(a.id);
              setAssignEmployeeIds([a.employeeId]);
              setAssignDate("");
              setAssignTime("");
              setAssignInstitution("");
              setAssignNotes("");
              setAssignSearch("");
              setAssignSubmitted(false);
              setAssignOpen(true);
            }}
          />
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <MedicalCheckHistoryTable
            assignments={historyAssignments}
            onOpen={(id) => { setDetailAssignmentId(id); setDetailOpen(true); }}
          />
        </TabsContent>

        {/* ── Settings ───────────────────────────────────────────────────── */}
        <TabsContent value="settings" className="space-y-4">
          {/* Company defaults */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Settings2 className="h-4 w-4" />{t("medicalCheckups.settings.companyDefaults")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">{t("medicalCheckups.settings.companyDefaultsDesc")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SettingField
                  label={t("medicalCheckups.settings.intervalMonths") as string}
                  value={settingsDrafts.intervalMonths ?? String(settings?.intervalMonths ?? 12)}
                  onChange={(v) => setSettingsDrafts(s => ({ ...s, intervalMonths: v }))}
                />
                <SettingField
                  label={t("medicalCheckups.settings.graceMonths") as string}
                  value={settingsDrafts.graceMonths ?? String(settings?.graceMonths ?? 3)}
                  onChange={(v) => setSettingsDrafts(s => ({ ...s, graceMonths: v }))}
                />
                <SettingField
                  label={t("medicalCheckups.settings.leadMonths") as string}
                  value={settingsDrafts.leadMonths ?? String(settings?.leadMonths ?? 3)}
                  onChange={(v) => setSettingsDrafts(s => ({ ...s, leadMonths: v }))}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  disabled={!companySettingsHaveChanges || companySettingsMutation.isPending}
                  onClick={() => {
                    const payload: any = {};
                    if (settingsDrafts.intervalMonths !== undefined) payload.intervalMonths = Number(settingsDrafts.intervalMonths);
                    if (settingsDrafts.graceMonths !== undefined) payload.graceMonths = Number(settingsDrafts.graceMonths);
                    if (settingsDrafts.leadMonths !== undefined) payload.leadMonths = Number(settingsDrafts.leadMonths);
                    companySettingsMutation.mutate(payload);
                  }}
                >
                  {t("common.save")}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Department overrides */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4" />{t("medicalCheckups.settings.departmentOverrides")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("medicalCheckups.table.department")}</TableHead>
                    <TableHead className="w-40">{t("medicalCheckups.table.frequency")}</TableHead>
                    <TableHead className="w-64 text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {departments.map(dept => {
                    const rule = departmentRules.find(r => r.departmentName === dept);
                    const value = deptDrafts[dept] ?? (rule ? String(rule.frequencyMonths) : "");
                    const hasChanges = deptDrafts[dept] !== undefined && value !== (rule ? String(rule.frequencyMonths) : "");
                    return (
                      <TableRow key={dept}>
                        <TableCell><div className="flex items-center gap-2">{dept}{rule && <Badge variant="outline">{t("medicalCheckups.settings.customRule")}</Badge>}</div></TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={1}
                            max={120}
                            placeholder={String(settings?.intervalMonths ?? 12)}
                            value={value}
                            onChange={(e) => setDeptDrafts(s => ({ ...s, [dept]: e.target.value }))}
                          />
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {rule && (
                            <Button size="sm" variant="ghost" onClick={() => deleteRuleMutation.mutate({ departmentId: rule.departmentId! })}>
                              {t("medicalCheckups.actions.resetToDefault")}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!hasChanges || !value || deptRuleMutation.isPending}
                            onClick={() => {
                              const id = rule?.departmentId ?? departmentIdByName.get(dept);
                              if (!id) return toast.error(t("medicalCheckups.messages.departmentMissing"));
                              deptRuleMutation.mutate({ departmentId: Number(id), frequencyMonths: Number(value) });
                            }}
                          >
                            {t("common.save")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {departments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">
                        {t("medicalCheckups.settings.noDepartments")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Team overrides */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />{t("medicalCheckups.settings.teamOverrides")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("medicalCheckups.table.team")}</TableHead>
                    <TableHead className="w-40">{t("medicalCheckups.table.frequency")}</TableHead>
                    <TableHead className="w-64 text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map(team => {
                    const rule = teamRules.find(r => r.teamName === team);
                    const value = teamDrafts[team] ?? (rule ? String(rule.frequencyMonths) : "");
                    const hasChanges = teamDrafts[team] !== undefined && value !== (rule ? String(rule.frequencyMonths) : "");
                    return (
                      <TableRow key={team}>
                        <TableCell><div className="flex items-center gap-2">{team}{rule && <Badge variant="outline">{t("medicalCheckups.settings.customRule")}</Badge>}</div></TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={1}
                            max={120}
                            placeholder={String(settings?.intervalMonths ?? 12)}
                            value={value}
                            onChange={(e) => setTeamDrafts(s => ({ ...s, [team]: e.target.value }))}
                          />
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {rule && (
                            <Button size="sm" variant="ghost" onClick={() => deleteRuleMutation.mutate({ teamName: team })}>
                              {t("medicalCheckups.actions.resetToDefault")}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!hasChanges || !value || teamRuleMutation.isPending}
                            onClick={() => teamRuleMutation.mutate({ teamName: team, frequencyMonths: Number(value) })}
                          >
                            {t("common.save")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {teams.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">
                        {t("medicalCheckups.settings.noTeams")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Position overrides */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Briefcase className="h-4 w-4" />{t("medicalCheckups.settings.positionOverrides")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("medicalCheckups.table.position")}</TableHead>
                    <TableHead className="w-40">{t("medicalCheckups.table.frequency")}</TableHead>
                    <TableHead className="w-64 text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {positions.map(pos => {
                    const rule = positionRules.find(r => r.positionId === pos.id);
                    const value = positionDrafts[pos.id] ?? (rule ? String(rule.frequencyMonths) : "");
                    const hasChanges = positionDrafts[pos.id] !== undefined && value !== (rule ? String(rule.frequencyMonths) : "");
                    return (
                      <TableRow key={pos.id}>
                        <TableCell><div className="flex items-center gap-2">{pos.title}{rule && <Badge variant="outline">{t("medicalCheckups.settings.customRule")}</Badge>}</div></TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={1}
                            max={120}
                            placeholder={String(settings?.intervalMonths ?? 12)}
                            value={value}
                            onChange={(e) => setPositionDrafts(s => ({ ...s, [pos.id]: e.target.value }))}
                          />
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {rule && (
                            <Button size="sm" variant="ghost" onClick={() => deleteRuleMutation.mutate({ positionId: pos.id })}>
                              {t("medicalCheckups.actions.resetToDefault")}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!hasChanges || !value || positionRuleMutation.isPending}
                            onClick={() => positionRuleMutation.mutate({ positionId: pos.id, frequencyMonths: Number(value) })}
                          >
                            {t("common.save")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {positions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">
                        {t("medicalCheckups.settings.noPositions")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );

  // ── Employee personal view ──────────────────────────────────────────────────
  const employeeView = (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <HeartPulse className="h-4 w-4" />{t("medicalCheckups.myRecord.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {myRecord ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-muted-foreground">{t("medicalCheckups.table.status")}</p>
              <div className="mt-1"><StatusBadge status={myRecord.status} /></div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("medicalCheckups.table.lastCheck")}</p>
              <p className="text-sm font-medium">{fmtDate(myRecord.lastCheckDate)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("medicalCheckups.table.nextDue")}</p>
              <p className="text-sm font-medium">{fmtDate(myRecord.nextDueDate)}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("medicalCheckups.myRecord.empty")}</p>
        )}

        <div>
          <p className="text-sm font-semibold mb-2">{t("medicalCheckups.myRecord.pendingAssignments")}</p>
          {myPendingAssignments.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t("medicalCheckups.myRecord.noPending")}</p>
          ) : (
            <div className="space-y-2">
              {myPendingAssignments.map(a => (
                <div key={a.id} className="rounded-lg border p-3 flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
                  <div>
                    <p className="text-sm font-medium">{a.institution}</p>
                    <p className="text-xs text-muted-foreground">{fmtDateTime(a.scheduledDate, a.scheduledTime)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.status === "pending" && (
                      <>
                        <Button size="sm" onClick={() => respondMutation.mutate({ id: a.id, action: "accept" })}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.actions.accept")}
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => { setRejectAssignmentId(a.id); setRejectReason(""); setRejectOpen(true); }}>
                          <XCircle className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.actions.reject")}
                        </Button>
                      </>
                    )}
                    {a.status === "accepted" && <Badge variant="outline">{t("medicalCheckups.assignmentStatus.accepted")}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <AppLayout>
      <div className="container mx-auto py-6 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <HeartPulse className="h-6 w-6 text-primary" />{t("medicalCheckups.title")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("medicalCheckups.subtitle")}</p>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <StatCardsSkeleton count={4} />
            <TableSkeleton rows={6} cols={5} />
          </div>
        ) : isError ? (
          <Card className="border-destructive/30">
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <CircleAlert className="h-8 w-8 text-destructive" />
              <div className="space-y-1">
                <p className="font-semibold">{t("medicalCheckups.loadError.title")}</p>
                <p className="max-w-xl text-sm text-muted-foreground">
                  {t("medicalCheckups.loadError.description")}
                </p>
              </div>
              <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
                <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                {t("medicalCheckups.loadError.retry")}
              </Button>
            </CardContent>
          </Card>
        ) : isHrAdmin ? hrView : employeeView}
      </div>

      {/* ── Assign dialog (HR) ─────────────────────────────────────────────── */}
      <Dialog open={assignOpen} onOpenChange={(open) => { setAssignOpen(open); if (!open) setReschedulingFromId(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl sm:max-w-5xl max-h-[90dvh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <HeartPulse className="h-4 w-4 text-primary" />
              {t("medicalCheckups.assignDialog.title")}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,1fr)] flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">
            {/* Left column — schedule details */}
            <div className="min-w-0 lg:border-r">
              <ScrollArea className="h-full">
                <div className="px-6 py-5 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>{t("medicalCheckups.assignDialog.date")}</Label>
                      <DatePicker value={assignDate} onChange={setAssignDate} disablePastDates />
                      {assignSubmitted && assignmentErrors.date && <p className="text-xs text-destructive">{t("medicalCheckups.validation.dateRequired")}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t("medicalCheckups.assignDialog.time")}</Label>
                      <Time24Picker value={assignTime} onChange={setAssignTime} />
                      {assignSubmitted && assignmentErrors.time && <p className="text-xs text-destructive">{t("medicalCheckups.validation.timeRequired")}</p>}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label>{t("medicalCheckups.assignDialog.institution")}</Label>
                    <Input
                      value={assignInstitution}
                      onChange={(e) => setAssignInstitution(e.target.value)}
                      placeholder={t("medicalCheckups.assignDialog.institutionPlaceholder") as string}
                    />
                    {assignSubmitted && assignmentErrors.institution && <p className="text-xs text-destructive">{t("medicalCheckups.validation.institutionRequired")}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label>{t("medicalCheckups.assignDialog.notes")}</Label>
                    <Textarea
                      value={assignNotes}
                      onChange={(e) => setAssignNotes(e.target.value)}
                      placeholder={t("medicalCheckups.assignDialog.notesPlaceholder") as string}
                      rows={4}
                    />
                    <p className="text-[11px] text-muted-foreground">{t("medicalCheckups.assignDialog.notesHint")}</p>
                  </div>
                </div>
              </ScrollArea>
            </div>

            {/* Right column — employee picker */}
            <div className="min-w-0 flex flex-col min-h-[18rem] lg:min-h-0 overflow-hidden">
              <div className="px-4 pt-5 pb-3 shrink-0">
                <p className="text-sm font-medium mb-2">
                  {t("medicalCheckups.assignDialog.selectEmployees")}
                  {assignEmployeeIds.length > 0 && (
                    <Badge variant="secondary" className="ml-2 text-[10px]">{assignEmployeeIds.length}</Badge>
                  )}
                </p>
                {assignSubmitted && assignmentErrors.employees && <p className="mb-2 text-xs text-destructive">{t("medicalCheckups.validation.employeeRequired")}</p>}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    value={assignSearch}
                    onChange={(e) => setAssignSearch(e.target.value)}
                    placeholder={t("medicalCheckups.assignDialog.searchEmployees") as string}
                    className="pl-8 h-9"
                  />
                </div>
              </div>
              <ScrollArea className="flex-1 min-w-0 border-t [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:min-w-0">
                <div className="w-full max-w-full min-w-0 divide-y overflow-hidden">
                  {assignCandidates.map(e => {
                    const checked = assignEmployeeIds.includes(e.employeeId);
                    return (
                      <label key={e.employeeId} className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 py-2.5 pl-4 pr-6 cursor-pointer hover:bg-muted/50">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            const next = v ? [...assignEmployeeIds, e.employeeId] : assignEmployeeIds.filter(id => id !== e.employeeId);
                            setAssignEmployeeIds(next);
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{e.employeeName}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {[e.department, e.team].filter(Boolean).join(" · ") || "—"}
                          </p>
                        </div>
                        <span className="min-w-max justify-self-end"><StatusBadge status={e.status} /></span>
                      </label>
                    );
                  })}
                  {assignCandidates.length === 0 && (
                    <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {isError
                        ? t("medicalCheckups.loadError.description")
                        : t("medicalCheckups.assignDialog.noEmployees")}
                    </p>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setAssignOpen(false)}>{t("common.cancel")}</Button>
            <Button
              disabled={assignMutation.isPending}
              onClick={() => {
                setAssignSubmitted(true);
                if (Object.values(assignmentErrors).some(Boolean)) {
                  toast.error(t("medicalCheckups.validation.completeRequired"));
                  return;
                }
                assignMutation.mutate({
                  employeeIds: assignEmployeeIds,
                  scheduledDate: assignDate,
                  scheduledTime: assignTime,
                  institution: assignInstitution.trim(),
                  notes: assignNotes.trim() || null,
                });
              }}
            >
              <Send className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.actions.assignNew")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingAppointment} onOpenChange={(open) => { if (!open) setEditingAppointment(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              {editingAppointment?.scope === "group"
                ? t("medicalCheckups.editDialog.groupTitle")
                : t("medicalCheckups.editDialog.individualTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("medicalCheckups.assignDialog.date")}</Label>
                <DatePicker value={editDate} onChange={setEditDate} disablePastDates />
              </div>
              <div className="space-y-1.5">
                <Label>{t("medicalCheckups.assignDialog.time")}</Label>
                <Time24Picker value={editTime} onChange={setEditTime} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("medicalCheckups.assignDialog.institution")}</Label>
              <Input value={editInstitution} onChange={(event) => setEditInstitution(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("medicalCheckups.assignDialog.notes")}</Label>
              <Textarea value={editNotes} onChange={(event) => setEditNotes(event.target.value)} rows={4} />
            </div>
            {editingAppointment?.scope === "group" && (
              <p className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t("medicalCheckups.editDialog.groupHint")}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAppointment(null)}>{t("common.cancel")}</Button>
            <Button
              disabled={!editDate || !editTime || !editInstitution.trim() || updateAppointmentMutation.isPending}
              onClick={() => {
                if (!editingAppointment) return;
                updateAppointmentMutation.mutate({
                  id: editingAppointment.assignment.id,
                  scope: editingAppointment.scope,
                  scheduledDate: editDate,
                  scheduledTime: editTime,
                  institution: editInstitution.trim(),
                  notes: editNotes.trim() || null,
                });
              }}
            >
              {t("medicalCheckups.actions.saveChanges")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail dialog (opens via ?assignment=<id> from notification) ───── */}
      <AssignmentDetailDialog
        open={detailOpen}
        assignmentId={detailAssignmentId}
        legacyEmployeeId={searchParams.get("legacyEmployeeId") === "true"}
        assignments={assignments}
        currentUserEmployeeId={currentUserEmployeeId}
        onClose={closeDetail}
        onAccept={(id) => respondMutation.mutate({ id, action: "accept" })}
        onReject={(id) => { setRejectAssignmentId(id); setRejectReason(""); setRejectOpen(true); closeDetail(); }}
        onComplete={(id) => {
          const match = assignments.find(x => x.id === id);
          if (match) {
            setCompletingAssignment(match);
            closeDetail();
          }
        }}
      />

      {/* ── Reject dialog (employee) ───────────────────────────────────────── */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <CircleAlert className="h-4 w-4" />{t("medicalCheckups.rejectDialog.title")}
            </DialogTitle>
          </DialogHeader>
          <div>
            <p className="text-sm text-muted-foreground mb-2">{t("medicalCheckups.rejectDialog.description")}</p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("medicalCheckups.rejectDialog.placeholder") as string}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>{t("common.cancel")}</Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim() || respondMutation.isPending}
              onClick={() => {
                if (rejectAssignmentId == null) return;
                respondMutation.mutate({ id: rejectAssignmentId, action: "reject", rejectionReason: rejectReason.trim() });
              }}
            >
              {t("medicalCheckups.actions.confirmReject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Complete dialog (HR Upload and Result Select) ── */}
      <Dialog open={!!completingAssignment} onOpenChange={(o) => { if (!o) { setCompletingAssignment(null); setCompleteFile(null); setCompleteResult("passed"); } }}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              {t("medicalCheckups.completeDialog.title")}
            </DialogTitle>
          </DialogHeader>
          {completingAssignment && (
            <div className="space-y-4 py-2">
              <div className="text-xs bg-muted/50 rounded-xl p-3 space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("medicalCheckups.table.employee")}:</span>
                  <span className="font-semibold text-foreground">{completingAssignment.employeeName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("medicalCheckups.table.scheduled")}:</span>
                  <span className="text-foreground">{fmtDateTime(completingAssignment.scheduledDate, completingAssignment.scheduledTime)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("medicalCheckups.table.institution")}:</span>
                  <span className="text-foreground">{completingAssignment.institution}</span>
                </div>
              </div>

              {!completingAssignment.canComplete && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  {t("medicalCheckups.messages.tooEarly")}
                </p>
              )}

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground">
                  {t("medicalCheckups.completeDialog.resultLabel")} <span className="text-red-500">*</span>
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setCompleteResult("passed")}
                    className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border text-xs font-medium transition-all ${
                      completeResult === "passed"
                        ? "border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                        : "border-muted hover:bg-muted/50 text-muted-foreground bg-card"
                    }`}
                  >
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    {t("medicalCheckups.completeDialog.passed")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCompleteResult("failed")}
                    className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border text-xs font-medium transition-all ${
                      completeResult === "failed"
                        ? "border-rose-500 bg-rose-500/10 text-rose-700 dark:text-rose-400"
                        : "border-muted hover:bg-muted/50 text-muted-foreground bg-card"
                    }`}
                  >
                    <XCircle className="h-4 w-4 text-rose-500" />
                    {t("medicalCheckups.completeDialog.failed")}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground">
                  {t("medicalCheckups.completeDialog.documentLabel")}
                </Label>
                <div
                  onClick={() => completeFileInputRef.current?.click()}
                  className="border-2 border-dashed border-muted-foreground/20 hover:border-primary/50 transition-colors rounded-xl p-6 text-center cursor-pointer space-y-2 bg-card"
                >
                  <input
                    type="file"
                    ref={completeFileInputRef}
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setCompleteFile(e.target.files[0]);
                      }
                    }}
                    accept=".pdf,image/*"
                    className="hidden"
                  />
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    {completeFile
                      ? `${t("medicalCheckups.completeDialog.fileSelected")} ${completeFile.name}`
                      : t("medicalCheckups.completeDialog.uploadPlaceholder")}
                  </p>
                </div>
              </div>

              <DialogFooter className="flex items-center gap-2 pt-2 sm:justify-end">
                <Button
                  variant="outline"
                  className="flex-1 sm:flex-initial h-9 text-xs"
                  onClick={() => {
                    setCompletingAssignment(null);
                    setCompleteFile(null);
                    setCompleteResult("passed");
                  }}
                  disabled={isSubmittingComplete || !completingAssignment.canComplete}
                >
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button
                  className="flex-1 sm:flex-initial h-9 text-xs bg-primary hover:bg-primary/95 text-primary-foreground"
                  disabled={isSubmittingComplete}
                  onClick={async () => {
                    if (!completingAssignment) return;
                    setIsSubmittingComplete(true);
                    try {
                      let docId: number | null = null;
                      if (completeFile) {
                        // Create HR document
                        const doc = await apiCreateHRDocument({
                          employeeId: completingAssignment.employeeId,
                          documentName: t("medicalCheckups.completeDialog.documentName", {
                            employeeName: completingAssignment.employeeName,
                            date: fmtDate(completingAssignment.scheduledDate),
                          }),
                          documentType: "Medical Check",
                        });
                        // Upload file
                        await apiUploadHRDocumentFile(doc.id, completeFile);
                        docId = doc.id;
                      }

                      await completeMutation.mutateAsync({
                        id: completingAssignment.id,
                        result: completeResult,
                        supportingDocumentId: docId,
                      });
                    } catch (err: any) {
                      toast.error(localizedMedicalError(err, "medicalCheckups.messages.completeFailed"));
                    } finally {
                      setIsSubmittingComplete(false);
                    }
                  }}
                >
                  {isSubmittingComplete && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("medicalCheckups.completeDialog.submit")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function Time24Picker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useTranslation();
  const [hour = "", minute = ""] = value ? value.split(":") : [];
  const hours = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));
  return (
    <div className="grid h-10 grid-cols-[1fr_auto_1fr] items-center rounded-md border border-input bg-background px-2 shadow-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
      <select
        aria-label={t("medicalCheckups.assignDialog.hour") as string}
        className="h-9 min-w-0 rounded bg-background px-2 text-center text-sm text-foreground outline-none [color-scheme:light] dark:[color-scheme:dark] focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 [&>option]:bg-background [&>option]:text-foreground"
        value={hour}
        onChange={(event) => onChange(`${event.target.value}:${minute || "00"}`)}
      >
        <option value="">--</option>
        {hours.map(item => <option key={item} value={item}>{item}</option>)}
      </select>
      <span className="text-sm font-semibold text-muted-foreground">:</span>
      <select
        aria-label={t("medicalCheckups.assignDialog.minute") as string}
        className="h-9 min-w-0 rounded bg-background px-2 text-center text-sm text-foreground outline-none [color-scheme:light] dark:[color-scheme:dark] focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 [&>option]:bg-background [&>option]:text-foreground"
        value={minute}
        onChange={(event) => onChange(`${hour || "00"}:${event.target.value}`)}
      >
        <option value="">--</option>
        {minutes.map(item => <option key={item} value={item}>{item}</option>)}
      </select>
    </div>
  );
}

function SettingField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <p className="text-xs font-medium mb-1">{label}</p>
      <Input type="number" min={0} max={120} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// Status row config: icon + badge background — Organization-style
const STATUS_ROW_CONFIG: Record<MedicalCheckStatus, { icon: any; bg: string }> = {
  valid:                { icon: ShieldCheck,   bg: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  due_soon:             { icon: CalendarClock, bg: "bg-amber-100 text-amber-700 border-amber-200" },
  expired:              { icon: AlertTriangle, bg: "bg-red-100 text-red-700 border-red-200" },
  failed:               { icon: XCircle,      bg: "bg-rose-100 text-rose-700 border-rose-200" },
  no_exam:              { icon: FileText,     bg: "bg-slate-100 text-slate-700 border-slate-200" },
};

function StatusBadgeWithIcon({ status, label }: { status: MedicalCheckStatus; label: string }) {
  const sc = STATUS_ROW_CONFIG[status];
  const Icon = sc.icon;
  return (
    <Badge variant="outline" className={`text-[10px] border ${sc.bg}`}>
      <Icon className="h-3 w-3 mr-0.5" />{label}
    </Badge>
  );
}

const ASSIGNMENT_STATUS_CONFIG: Record<string, { bg: string }> = {
  pending:   { bg: "bg-amber-100 text-amber-700 border-amber-200" },
  accepted:  { bg: "bg-blue-100 text-blue-700 border-blue-200" },
  attended:  { bg: "bg-violet-100 text-violet-700 border-violet-200" },
  absent:    { bg: "bg-slate-100 text-slate-700 border-slate-200" },
  rejected:  { bg: "bg-red-100 text-red-700 border-red-200" },
  completed: { bg: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  cancelled: { bg: "bg-slate-100 text-slate-700 border-slate-200" },
  passed:    { bg: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  failed:    { bg: "bg-rose-100 text-rose-700 border-rose-200" },
};

function RecordsTable({ records }: { records: MedicalCheckEmployeeRecord[] }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
            <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("medicalCheckups.table.employee")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">{t("medicalCheckups.table.department")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">{t("medicalCheckups.table.lastCheck")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("medicalCheckups.table.nextDue")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden xl:table-cell">{t("medicalCheckups.table.frequency")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("medicalCheckups.table.complianceStatus")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="py-12">
                <div className="flex flex-col items-center gap-3 text-center">
                  <HeartPulse className="h-8 w-8 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">{t("medicalCheckups.overview.empty")}</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            records.map(r => (
              <TableRow key={r.employeeId} className="border-b border-border/40 transition-colors">
                <TableCell className="pl-4 py-3">
                  <div className="flex items-center gap-3">
                    <InitialsAvatar name={r.employeeName} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.employeeName}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.employeeEmail ?? ""}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-3 hidden md:table-cell">
                  <div className="flex flex-col">
                    <span className="text-sm text-foreground">{r.department ?? "—"}</span>
                    {r.team && <span className="text-xs text-muted-foreground">{r.team}</span>}
                  </div>
                </TableCell>
                <TableCell className="py-3 hidden lg:table-cell">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-muted-foreground">{fmtDate(r.latestExamDate ?? r.lastCheckDate)}</span>
                    {r.latestResult && <span className={`text-[10px] ${r.latestResult === "failed" ? "text-rose-700" : "text-emerald-700"}`}>{t(`medicalCheckups.assignmentStatus.${r.latestResult}`)}</span>}
                  </div>
                </TableCell>
                <TableCell className="py-3">
                  <span className="text-sm text-foreground">{fmtDate(r.nextDueDate)}</span>
                </TableCell>
                <TableCell className="py-3 hidden xl:table-cell">
                  <div className="flex items-center gap-2">
                    <span className="text-sm tabular-nums">{r.frequencyMonths}m</span>
                    <SourceBadge record={r} />
                  </div>
                </TableCell>
                <TableCell className="py-3">
                  <div className="flex flex-col items-start gap-1.5">
                    <StatusBadgeWithIcon status={r.status} label={t(`medicalCheckups.status.${r.status}`)} />
                    {r.appointmentStatus && (
                      <div className="flex flex-col items-start gap-0.5">
                        <Badge
                          variant="outline"
                          className={`text-[10px] border ${r.appointmentStatus === "accepted"
                            ? "bg-blue-100 text-blue-700 border-blue-200"
                            : "bg-amber-100 text-amber-700 border-amber-200"}`}
                        >
                          {t("medicalCheckups.table.appointmentStatus", {
                            status: t(`medicalCheckups.assignmentStatus.${r.appointmentStatus}`),
                          })}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">
                          {fmtDateTime(r.appointmentDate, r.appointmentTime)}
                          {r.appointmentInstitution ? ` · ${r.appointmentInstitution}` : ""}
                        </span>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function AssignmentsTable({
  assignments,
  allAssignments,
  employees,
  onOpen,
  onCancel,
  onCancelGroup,
  onEdit,
  onRecordAttendance,
  onAddToGroup,
  onAddNewToSlot,
  onRemoveFromGroup,
}: {
  assignments: MedicalCheckAssignment[];
  allAssignments: MedicalCheckAssignment[];
  employees: MedicalCheckEmployeeRecord[];
  onOpen: (id: number) => void;
  onCancel: (id: number) => void;
  onCancelGroup: (id: number) => void;
  onEdit: (assignment: MedicalCheckAssignment, scope: "individual" | "group") => void;
  onRecordAttendance: (groupId: number, attendeeAssignmentIds: number[]) => Promise<unknown>;
  onAddToGroup: (groupId: number, employeeIds: number[]) => void;
  onAddNewToSlot: (slot: { scheduledDate: string; scheduledTime: string | null; institution: string; notes: string | null; employeeIds: number[] }) => void;
  onRemoveFromGroup: (groupId: number, assignmentId: number) => void;
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // addGroupKey = visual group key (date|time|institution); null = dialog closed
  const [addGroupKey, setAddGroupKey] = useState<string | null>(null);
  // canonical groupId for the open dialog (null = all members are legacy solo)
  const [addCanonicalGroupId, setAddCanonicalGroupId] = useState<number | null>(null);
  // slot info carried into the dialog for the fallback "create new" path
  const [addSlot, setAddSlot] = useState<{ scheduledDate: string; scheduledTime: string | null; institution: string; notes: string | null } | null>(null);
  const [addSearch, setAddSearch] = useState("");
  const [addSelectedIds, setAddSelectedIds] = useState<number[]>([]);

  // Complete-group dialog state
  const [completeGroupKey, setCompleteGroupKey] = useState<string | null>(null);
  const [completeSelectedIds, setCompleteSelectedIds] = useState<Set<number>>(new Set());
  const [isCompleting, setIsCompleting] = useState(false);

  // Group visually by date + time + institution so assignments created
  // separately for the same appointment slot appear together.
  const groups = useMemo(() => {
    const map = new Map<string, MedicalCheckAssignment[]>();
    for (const a of assignments) {
      const key = `${a.scheduledDate}|${a.scheduledTime ?? ""}|${a.institution}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    return Array.from(map.entries()).sort(([, a], [, b]) =>
      (a[0]?.scheduledDate ?? "").localeCompare(b[0]?.scheduledDate ?? ""),
    );
  }, [assignments]);

  // Exclude employees already in the visual group from the picker
  const groupMemberIds = useMemo(() => {
    if (!addGroupKey) return new Set<number>();
    if (addCanonicalGroupId != null) {
      return new Set(
        allAssignments
          .filter(member => member.groupId === addCanonicalGroupId && member.status !== "cancelled")
          .map(member => member.employeeId),
      );
    }
    const group = groups.find(([k]) => k === addGroupKey);
    return new Set((group?.[1] ?? []).map(m => m.employeeId));
  }, [groups, addGroupKey, addCanonicalGroupId, allAssignments]);

  const addCandidates = useMemo(() => {
    const q = addSearch.trim().toLowerCase();
    return employees.filter(e =>
      !groupMemberIds.has(e.employeeId) &&
      (!q ||
        e.employeeName.toLowerCase().includes(q) ||
        (e.department ?? "").toLowerCase().includes(q) ||
        (e.team ?? "").toLowerCase().includes(q)),
    );
  }, [employees, groupMemberIds, addSearch]);

  function toggleCollapse(key: string) {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function openAddDialog(key: string, canonicalGroupId: number | null, slot: { scheduledDate: string; scheduledTime: string | null; institution: string; notes: string | null }) {
    setAddGroupKey(key);
    setAddCanonicalGroupId(canonicalGroupId);
    setAddSlot(slot);
    setAddSearch("");
    setAddSelectedIds([]);
  }

  function closeAddDialog() {
    setAddGroupKey(null);
    setAddCanonicalGroupId(null);
    setAddSlot(null);
  }

  return (
    <>
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
              <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("medicalCheckups.table.employee")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">{t("medicalCheckups.table.status")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide text-right pr-4">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="py-12">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <CalendarClock className="h-8 w-8 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">{t("medicalCheckups.assignments.empty")}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              groups.flatMap(([key, members]) => {
                const isCollapsed = collapsed.has(key);
                const first = members[0];
                // Pick the first non-null groupId as the canonical one for "add employees"
                const groupId = members.find(m => m.groupId != null)?.groupId ?? null;
                const isSolo = groupId == null;
                const rows = [];

                const acceptedMembers = members.filter(m => m.status === "accepted");
                const hasMutableAppointments = members.some(m => m.status === "pending" || m.status === "accepted");

                // ── Group header row ───────────────────────────────────────
                rows.push(
                  <TableRow key={`gh-${key}`} className="group/gh bg-muted/20 border-b border-border/40 hover:bg-muted/30">
                    <TableCell colSpan={3} className="py-2.5 pl-4 pr-4">
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => toggleCollapse(key)}
                        >
                          {isCollapsed
                            ? <ChevronRight className="h-4 w-4" />
                            : <ChevronDown className="h-4 w-4" />}
                        </button>
                        <span className="flex items-center gap-1.5 text-sm font-medium">
                          <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                          {fmtDateTime(first.scheduledDate, first.scheduledTime)}
                        </span>
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5" />
                          {first.institution}
                        </span>
                        <Badge variant="secondary" className="text-[10px]">
                          {members.length} {members.length === 1 ? t("medicalCheckups.table.employee").toLowerCase() : t("medicalCheckups.table.employees").toLowerCase()}
                        </Badge>
                        {/* Group-level actions — always visible on the header */}
                        <div className="ml-auto flex items-center gap-2">
                          {hasMutableAppointments && groupId != null && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                              onClick={() => {
                                setCompleteGroupKey(key);
                                setCompleteSelectedIds(new Set(acceptedMembers.filter(m => m.canComplete).map(m => m.id)));
                              }}
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              {t("medicalCheckups.actions.recordAttendance")}
                            </Button>
                          )}
                          {hasMutableAppointments && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs gap-1"
                                onClick={() => openAddDialog(key, groupId, {
                                  scheduledDate: first.scheduledDate,
                                  scheduledTime: first.scheduledTime,
                                  institution: first.institution,
                                  notes: first.notes,
                                })}
                              >
                                <UserPlus className="h-3 w-3" />
                                {t("medicalCheckups.groupCard.addEmployees")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs gap-1"
                                onClick={() => onEdit(first, isSolo ? "individual" : "group")}
                              >
                                <Pencil className="h-3 w-3" />
                                {isSolo ? t("medicalCheckups.actions.editIndividual") : t("medicalCheckups.actions.editGroup")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs gap-1 text-destructive border-destructive/30"
                                onClick={() => isSolo ? onCancel(first.id) : onCancelGroup(first.id)}
                              >
                                <CalendarX className="h-3 w-3" />
                                {isSolo ? t("medicalCheckups.actions.cancelIndividual") : t("medicalCheckups.actions.cancelGroup")}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>,
                );

                // ── Member rows ────────────────────────────────────────────
                if (!isCollapsed) {
                  members.forEach(a => {
                    const sc = ASSIGNMENT_STATUS_CONFIG[a.status] ?? ASSIGNMENT_STATUS_CONFIG.pending;
                    rows.push(
                      <TableRow
                        key={a.id}
                        className="group/row border-b border-border/40 transition-colors cursor-pointer hover:bg-muted/40"
                        onClick={() => onOpen(a.id)}
                      >
                        <TableCell className="pl-8 py-3">
                          <div className="flex items-center gap-3">
                            <InitialsAvatar name={a.employeeName} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{a.employeeName}</p>
                              <p className="text-xs text-muted-foreground truncate">{a.department ?? ""}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-3 hidden md:table-cell">
                          <Badge variant="outline" className={`text-[10px] border ${sc.bg}`}>
                            {t(`medicalCheckups.assignmentStatus.${a.status}`)}
                          </Badge>
                        </TableCell>
                        {/* Actions — only visible on row hover */}
                        <TableCell className="py-3 pr-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
                            {(a.status === "pending" || a.status === "accepted") && <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                              title={t("medicalCheckups.actions.removeIndividual")}
                              onClick={() => a.groupId != null
                                ? onRemoveFromGroup(a.groupId, a.id)
                                : onCancel(a.id)
                              }
                            >
                              <UserMinus className="h-3.5 w-3.5 mr-1" />
                              {t("medicalCheckups.actions.removeIndividual")}
                            </Button>}
                          </div>
                        </TableCell>
                      </TableRow>,
                    );
                  });
                }

                return rows;
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Complete group dialog ── */}
      {(() => {
        const groupMembers = completeGroupKey
          ? (groups.find(([k]) => k === completeGroupKey)?.[1] ?? [])
          : [];
        const first = groupMembers[0];
        return (
          <Dialog open={completeGroupKey != null} onOpenChange={(o) => { if (!o) { setCompleteGroupKey(null); setCompleteSelectedIds(new Set()); } }}>
            <DialogContent className="w-full max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  {t("medicalCheckups.completeGroupDialog.title")}
                </DialogTitle>
                {first && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {fmtDateTime(first.scheduledDate, first.scheduledTime)} · {first.institution}
                  </p>
                )}
              </DialogHeader>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t("medicalCheckups.completeGroupDialog.subtitle")}</p>
                <div className="border rounded-lg divide-y mt-2 max-h-72 overflow-y-auto">
                  {groupMembers.map(a => {
                    const isAccepted = a.status === "accepted";
                    const isEligible = isAccepted && a.canComplete;
                    const checked = completeSelectedIds.has(a.id);
                    return (
                      <label
                        key={a.id}
                        className={`flex items-center gap-3 px-4 py-3 ${isEligible ? "cursor-pointer hover:bg-muted/50" : "opacity-50 cursor-not-allowed"}`}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={!isEligible}
                          onCheckedChange={(v) => {
                            setCompleteSelectedIds(prev => {
                              const next = new Set(prev);
                              if (v) next.add(a.id); else next.delete(a.id);
                              return next;
                            });
                          }}
                        />
                        <InitialsAvatar name={a.employeeName} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{a.employeeName}</p>
                          <p className="text-xs text-muted-foreground truncate">{a.department ?? ""}</p>
                        </div>
                        {!isEligible && (
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            {isAccepted ? t("medicalCheckups.actions.notYetAvailable") : t(`medicalCheckups.assignmentStatus.${a.status}`)}
                          </Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground pt-1">
                  {t("medicalCheckups.completeGroupDialog.hint")}
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setCompleteGroupKey(null); setCompleteSelectedIds(new Set()); }}>
                  {t("common.cancel")}
                </Button>
                <Button
                  disabled={isCompleting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={async () => {
                    setIsCompleting(true);
                    try {
                      const groupId = groupMembers.find(member => member.groupId != null)?.groupId;
                      if (groupId == null) return;
                      await onRecordAttendance(groupId, Array.from(completeSelectedIds));
                      setCompleteGroupKey(null);
                      setCompleteSelectedIds(new Set());
                    } finally {
                      setIsCompleting(false);
                    }
                  }}
                >
                  {isCompleting
                    ? <><span className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-1.5" />{t("common.saving")}</>
                    : <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />{t("medicalCheckups.completeGroupDialog.confirm")} ({completeSelectedIds.size})</>
                  }
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Add to group dialog */}
      <Dialog open={addGroupKey != null} onOpenChange={(o) => { if (!o) closeAddDialog(); }}>
        <DialogContent className="w-full max-w-sm max-h-[80dvh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />{t("medicalCheckups.addToGroupDialog.title")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            <p className="text-xs text-muted-foreground">{t("medicalCheckups.addToGroupDialog.selectEmployees")}</p>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={addSearch}
                onChange={(e) => setAddSearch(e.target.value)}
                placeholder={t("medicalCheckups.addToGroupDialog.searchEmployees") as string}
                className="pl-8 h-9"
              />
            </div>
            <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
              {addCandidates.map(e => {
                const checked = addSelectedIds.includes(e.employeeId);
                return (
                  <label key={e.employeeId} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/50">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        setAddSelectedIds(v
                          ? [...addSelectedIds, e.employeeId]
                          : addSelectedIds.filter(id => id !== e.employeeId));
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{e.employeeName}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[e.department, e.team].filter(Boolean).join(" · ") || "—"}
                      </p>
                    </div>
                    <StatusBadge status={e.status} />
                  </label>
                );
              })}
              {addCandidates.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">{t("medicalCheckups.addToGroupDialog.noEmployees")}</p>
              )}
            </div>
          </div>
          <DialogFooter className="shrink-0 pt-2">
            <Button variant="outline" onClick={closeAddDialog}>{t("common.cancel")}</Button>
            <Button
              disabled={addSelectedIds.length === 0}
              onClick={() => {
                if (!addGroupKey) return;
                if (addCanonicalGroupId != null) {
                  onAddToGroup(addCanonicalGroupId, addSelectedIds);
                } else if (addSlot) {
                  onAddNewToSlot({ ...addSlot, employeeIds: addSelectedIds });
                }
                closeAddDialog();
              }}
            >
              <UserPlus className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.groupCard.addEmployees")} ({addSelectedIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AssignmentDetailDialog({
  open, assignmentId, legacyEmployeeId, assignments, currentUserEmployeeId, onClose, onAccept, onReject, onComplete,
}: {
  open: boolean;
  assignmentId: number | null;
  legacyEmployeeId: boolean;
  assignments: MedicalCheckAssignment[];
  currentUserEmployeeId: number | null;
  onClose: () => void;
  onAccept: (id: number) => void;
  onReject: (id: number) => void;
  onComplete: (id: number) => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isHrAdmin = user?.role?.mainRole === "hr" || user?.role?.mainRole === "admin";
  const cachedAssignment = legacyEmployeeId ? null : assignments.find(x => x.id === assignmentId) ?? null;
  const detailQuery = useQuery({
    queryKey: ["medical-check-assignment", assignmentId, legacyEmployeeId],
    queryFn: () => apiGetMedicalCheckAssignment(assignmentId!, { legacyEmployeeId }),
    enabled: open && assignmentId != null,
    retry: false,
  });
  const a = detailQuery.data ?? cachedAssignment;
  // The assignee — regardless of role — is the only one who can Accept/Reject.
  // HR users who are also employees see these buttons on their own assignments.
  const isAssignee = a != null && currentUserEmployeeId != null && a.employeeId === currentUserEmployeeId;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HeartPulse className="h-4 w-4 text-primary" />{t("medicalCheckups.detailDialog.title")}
          </DialogTitle>
        </DialogHeader>

        {a ? (
          <div className="space-y-4">
            <div className="rounded-lg border bg-primary/5 p-3 flex gap-2">
              <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-xs text-foreground/80 leading-relaxed">
                {t(getMedicalCheckDetailDescriptionKey(a))}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">{t("medicalCheckups.table.employee")}</p>
                <p className="font-medium">{a.employeeName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("medicalCheckups.table.scheduledDateTime")}</p>
                <p className="font-medium flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                  {fmtDateTime(a.scheduledDate, a.scheduledTime)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("medicalCheckups.table.institution")}</p>
                <p className="font-medium flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  {a.institution}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("medicalCheckups.table.status")}</p>
                <Badge variant="outline">{t(`medicalCheckups.assignmentStatus.${a.status}`)}</Badge>
              </div>
              {a.createdByName && (
                <div>
                  <p className="text-xs text-muted-foreground">{t("medicalCheckups.detailDialog.scheduledBy")}</p>
                  <p className="font-medium">{a.createdByName}</p>
                </div>
              )}
            </div>

            {a.notes && (
              <div className="rounded-lg border p-3 bg-muted/40">
                <p className="text-xs font-semibold mb-1">{t("medicalCheckups.detailDialog.hrNotes")}</p>
                <p className="text-sm whitespace-pre-wrap">{a.notes}</p>
              </div>
            )}

            {a.status === "rejected" && a.rejectionReason && (
              <div className="rounded-lg border border-red-200 p-3 bg-red-50">
                <p className="text-xs font-semibold mb-1 text-red-700">{t("medicalCheckups.rejected.reason")}</p>
                <p className="text-sm whitespace-pre-wrap text-red-900">{a.rejectionReason}</p>
              </div>
            )}
            {["passed", "failed", "completed"].includes(a.status) && (
              <div className={`rounded-lg border p-3 ${a.status === "failed" ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}>
                <p className="text-xs font-semibold mb-1">{t("medicalCheckups.detailDialog.result")}</p>
                <p className="text-sm font-medium">{t(`medicalCheckups.assignmentStatus.${a.status}`)}</p>
              </div>
            )}
            {a.supportingDocumentId && (
              <button
                type="button"
                onClick={() => {
                  void apiDownloadHRDocumentFile(
                    a.supportingDocumentId!,
                    a.supportingDocumentName || "document",
                  ).catch((error) => {
                    console.error("[medical check document download] failed", error);
                    toast.error(t("documents.previewError.downloadFailed"));
                  });
                }}
                className="flex w-full items-center gap-2 rounded-lg border p-3 text-left text-sm text-primary hover:bg-muted/40"
              >
                <FileText className="h-4 w-4" />
                {a.supportingDocumentName || t("medicalCheckups.detailDialog.supportingDocument")}
              </button>
            )}
          </div>
        ) : detailQuery.isLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <p className="text-sm text-muted-foreground py-4">
            {(detailQuery.error as any)?.code === "MEDICAL_CHECK_ACCESS_DENIED"
              ? t("medicalCheckups.detailDialog.inaccessible")
              : t("medicalCheckups.detailDialog.notFound")}
          </p>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>{t("common.close")}</Button>
          {a && a.status === "pending" && isAssignee && (
            <>
              <Button variant="destructive" onClick={() => onReject(a.id)}>
                <XCircle className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.actions.reject")}
              </Button>
              <Button onClick={() => { onAccept(a.id); onClose(); }}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.actions.accept")}
              </Button>
            </>
          )}
          {a && (a.status === "accepted" || a.status === "attended") && isHrAdmin && (
            <Button disabled={!a.canComplete} title={!a.canComplete ? t("medicalCheckups.messages.tooEarly") : undefined} onClick={() => { onComplete(a.id); onClose(); }}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{t("medicalCheckups.actions.markCompleted")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectedTable({ assignments, openAssignments, locallyRescheduledIds, onOpen, onReschedule, onArchive }: {
  assignments: MedicalCheckAssignment[];
  openAssignments: MedicalCheckAssignment[];
  locallyRescheduledIds: Set<number>;
  onOpen: (id: number) => void;
  onReschedule: (a: MedicalCheckAssignment) => void;
  onArchive: (id: number) => void;
}) {
  const { t } = useTranslation();

  // A rejected assignment is considered "rescheduled" when:
  // (a) a newer pending/accepted assignment exists for the same employee in the server data, OR
  // (b) it was rescheduled in the current session (optimistic — no refetch wait needed).
  const serverRescheduledIds = useMemo(() => {
    const openByEmployee = new Map<number, MedicalCheckAssignment[]>();
    for (const o of openAssignments) {
      if (!openByEmployee.has(o.employeeId)) openByEmployee.set(o.employeeId, []);
      openByEmployee.get(o.employeeId)!.push(o);
    }
    const result = new Set<number>();
    for (const a of assignments) {
      const open = openByEmployee.get(a.employeeId) ?? [];
      if (open.some(o => new Date(o.createdAt) > new Date(a.createdAt))) {
        result.add(a.id);
      }
    }
    return result;
  }, [assignments, openAssignments]);

  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
            <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("medicalCheckups.table.employee")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">{t("medicalCheckups.table.scheduledDateTime")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">{t("medicalCheckups.table.institution")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("medicalCheckups.rejected.reason")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide text-right">{t("common.actions")}</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {assignments.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="py-12">
                <div className="flex flex-col items-center gap-3 text-center">
                  <XCircle className="h-8 w-8 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">{t("medicalCheckups.rejected.empty")}</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            assignments.map(a => {
              const isRescheduled = serverRescheduledIds.has(a.id) || locallyRescheduledIds.has(a.id);
              return (
                <TableRow
                  key={a.id}
                  className="border-b border-border/40 transition-colors cursor-pointer hover:bg-muted/40"
                  onClick={() => onOpen(a.id)}
                >
                  <TableCell className="pl-4 py-3">
                    <div className="flex items-center gap-3">
                      <InitialsAvatar name={a.employeeName} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{a.employeeName}</p>
                        <p className="text-xs text-muted-foreground truncate">{a.department ?? ""}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 hidden md:table-cell">
                    <span className="text-sm text-muted-foreground">{fmtDateTime(a.scheduledDate, a.scheduledTime)}</span>
                  </TableCell>
                  <TableCell className="py-3 hidden lg:table-cell">
                    <span className="text-sm text-muted-foreground">{a.institution}</span>
                  </TableCell>
                  <TableCell className="py-3 max-w-md">
                    <p className="text-sm text-red-700 whitespace-pre-wrap line-clamp-2">{a.rejectionReason}</p>
                  </TableCell>
                  <TableCell className="py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {isRescheduled ? (
                        <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-700 border-emerald-200 gap-1">
                          <CheckCircle2 className="h-3 w-3" />{t("medicalCheckups.rejected.rescheduled")}
                        </Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => onReschedule(a)}>
                          {t("medicalCheckups.actions.reschedule")}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => onArchive(a.id)}>
                        <Archive className="h-3.5 w-3.5" />
                        {t("medicalCheckups.actions.archive")}
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 w-10">
                    <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function MedicalCheckHistoryTable({ assignments, onOpen }: {
  assignments: MedicalCheckAssignment[];
  onOpen: (id: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead>{t("medicalCheckups.table.employee")}</TableHead>
            <TableHead>{t("medicalCheckups.table.scheduledDateTime")}</TableHead>
            <TableHead className="hidden md:table-cell">{t("medicalCheckups.table.institution")}</TableHead>
            <TableHead>{t("medicalCheckups.table.status")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {assignments.length === 0 ? (
            <TableRow><TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">{t("medicalCheckups.history.empty")}</TableCell></TableRow>
          ) : assignments.map(assignment => {
            const style = ASSIGNMENT_STATUS_CONFIG[assignment.status] ?? ASSIGNMENT_STATUS_CONFIG.cancelled;
            return (
              <TableRow key={assignment.id} className="cursor-pointer" onClick={() => onOpen(assignment.id)}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <InitialsAvatar name={assignment.employeeName} />
                    <span className="text-sm font-medium">{assignment.employeeName}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{fmtDateTime(assignment.scheduledDate, assignment.scheduledTime)}</TableCell>
                <TableCell className="hidden md:table-cell text-sm">{assignment.institution}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] ${style.bg}`}>
                    {assignment.archived
                      ? t("medicalCheckups.assignmentStatus.archived")
                      : t(`medicalCheckups.assignmentStatus.${assignment.status}`)}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
